import { ref, computed } from "vue";
import { defineStore } from "pinia";
import apiClient from "@/config/apiClient";

const STORAGE_KEY = "convena_current_org_id";

export interface Organization {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  plan: "free" | "pro" | "enterprise";
  // Phase 5.6: present on every org returned from /api/organization since
  // the Phase 5.2 migration. Optional in the type because legacy responses
  // (and other parts of the app constructing Organization-shaped objects)
  // may not have set it.
  onboarding_completed?: boolean;
}

export interface OrganizationWithRole extends Organization {
  pivot: {
    role: "owner" | "admin" | "staff" | "finance";
  };
}

export type OrgRole = OrganizationWithRole["pivot"]["role"];

export const useOrgStore = defineStore("org", () => {
  // --- state ---
  const currentOrg = ref<Organization | null>(null);
  const userOrgs = ref<OrganizationWithRole[]>([]);
  const isLoadingOrgs = ref(false);

  // --- actions ---

  /**
   * Fetch the user's accessible organizations from the backend.
   * Backend endpoint /api/organization sits outside org.context (it's the
   * source-of-truth for which orgs the user belongs to), so no
   * X-Organization-ID header is required here.
   */
  const fetchUserOrgs = async (): Promise<void> => {
    isLoadingOrgs.value = true;
    try {
      const response = await apiClient.get("/api/organization");
      userOrgs.value = response.data?.organizations ?? [];
    } finally {
      isLoadingOrgs.value = false;
    }
  };

  /**
   * Mark an org as the active one and persist its id to localStorage.
   * The role is intentionally NOT cached on currentOrg — it's derived
   * via the `currentRole` getter from userOrgs, so role updates from the
   * server (e.g. demotion) take effect on the next fetchUserOrgs().
   */
  const selectOrg = (org: OrganizationWithRole): void => {
    currentOrg.value = {
      id: org.id,
      name: org.name,
      slug: org.slug,
      is_active: org.is_active,
      plan: org.plan,
      // Phase 5.6: must be carried into currentOrg so the router guard can
      // gate /onboarding correctly. The Onboarding wizard mutates this
      // directly on success.
      onboarding_completed: org.onboarding_completed,
    };
    try {
      localStorage.setItem(STORAGE_KEY, String(org.id));
    } catch (e) {
      // localStorage unavailable (private mode / quota); selection still
      // works for the session but won't survive a reload. Not fatal.
    }
  };

  /**
   * Clear all org state. Called on logout, and when restoreOrg() finds
   * the stored id is no longer valid.
   */
  const clearOrg = (): void => {
    currentOrg.value = null;
    userOrgs.value = [];
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  };

  /**
   * On app boot, restore the previously-selected org from localStorage.
   * Validates the stored id against the user's accessible orgs — if the
   * user has lost access (removed from org, org deactivated), the stored
   * id is cleared and no org is auto-selected.
   *
   * Internally fetches userOrgs if not yet loaded, so this is safe to call
   * as a one-liner from app boot.
   */
  const restoreOrg = async (): Promise<void> => {
    let storedIdRaw: string | null = null;
    try {
      storedIdRaw = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return;
    }
    if (!storedIdRaw) return;

    const storedId = Number(storedIdRaw);
    if (!Number.isFinite(storedId)) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }
      return;
    }

    if (userOrgs.value.length === 0) {
      try {
        await fetchUserOrgs();
      } catch (e) {
        // Network/401/etc. — leave the stored id alone and don't auto-select.
        // The user will land on the org picker (or whatever screen handles
        // a missing currentOrg) and can retry.
        return;
      }
    }

    const found = userOrgs.value.find((o) => o.id === storedId);
    if (found) {
      selectOrg(found);
    } else {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }
    }
  };

  // --- getters ---

  const currentRole = computed<OrgRole | null>(() => {
    if (!currentOrg.value) return null;
    const match = userOrgs.value.find((o) => o.id === currentOrg.value!.id);
    return match?.pivot?.role ?? null;
  });

  const isOwner = computed<boolean>(() => currentRole.value === "owner");

  const isAdmin = computed<boolean>(
    () => currentRole.value === "admin" || isOwner.value,
  );

  const canManageEvents = computed<boolean>(() =>
    ["owner", "admin"].includes(currentRole.value ?? ""),
  );

  const canViewTreasury = computed<boolean>(() =>
    ["owner", "admin", "finance"].includes(currentRole.value ?? ""),
  );

  const canCreateParticipants = computed<boolean>(() =>
    ["owner", "admin", "staff"].includes(currentRole.value ?? ""),
  );

  const canVerifyPayments = computed<boolean>(() =>
    ["owner", "admin"].includes(currentRole.value ?? ""),
  );

  return {
    // state
    currentOrg,
    userOrgs,
    isLoadingOrgs,
    // actions
    fetchUserOrgs,
    selectOrg,
    clearOrg,
    restoreOrg,
    // getters
    currentRole,
    isOwner,
    isAdmin,
    canManageEvents,
    canViewTreasury,
    canCreateParticipants,
    canVerifyPayments,
  };
});
